import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, LoadingOverlay, PagePaper } from 'dashboard/components/common';
import TemplateEditor from './TemplateEditor';

/** Lists platform defaults + brand overrides in one merged view. */
export default function TemplatesTab({ brandId }) {
  const [brandTemplates, setBrandTemplates] = useState([]);
  const [platformDefaults, setPlatformDefaults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const load = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listBrandMailTemplates({ brand_id: brandId })
      .then((res) => {
        setBrandTemplates(Array.isArray(res?.brand) ? res.brand : []);
        setPlatformDefaults(Array.isArray(res?.platform_defaults) ? res.platform_defaults : []);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load templates', { toastId: 'mail-templates-load-error' }))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandMailTemplates({ brand_id: brandId })
      .then((res) => {
        if (cancelled) return;
        setBrandTemplates(Array.isArray(res?.brand) ? res.brand : []);
        setPlatformDefaults(Array.isArray(res?.platform_defaults) ? res.platform_defaults : []);
      })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load templates', { toastId: 'mail-templates-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const overrideMap = useMemo(() => {
    const m = {};
    brandTemplates.forEach((t) => { m[t.key] = t; });
    return m;
  }, [brandTemplates]);

  const rows = useMemo(() => platformDefaults.map((p) => ({
    key: p.key,
    subject: overrideMap[p.key]?.subject || p.subject,
    isOverride: !!overrideMap[p.key],
  })), [platformDefaults, overrideMap]);

  return (
    <PagePaper>
      <Typography variant='h6' fontWeight='700' mb='4px'>Templates</Typography>
      <Typography variant='body2' color='text.secondary' mb='20px'>
        Override any of the platform defaults to send fully on-brand emails.
      </Typography>
      <LoadingOverlay loading={loading && rows.length === 0} minHeight={160}>
      {!loading && rows.length === 0 && <EmptyState>No templates available.</EmptyState>}
      {rows.map((r) => (
        <Stack
          key={r.key}
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='10px'
          sx={{
            py: '12px', px: '14px',
            borderRadius: '10px',
            border: '1px solid', borderColor: 'divider',
            mb: '8px',
          }}
        >
          <Box minWidth={0}>
            <Stack direction='row' alignItems='center' gap='8px' mb='2px'>
              <Typography fontWeight='600'>{r.key}</Typography>
              <Chip
                size='small'
                color={r.isOverride ? 'secondary' : 'default'}
                label={r.isOverride ? 'Custom' : 'Default'}
              />
            </Stack>
            <Typography variant='body2' color='text.secondary' noWrap>{r.subject}</Typography>
          </Box>
          <Tooltip title='Edit'>
            <IconButton size='small' onClick={() => setEditingKey(r.key)}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
      </LoadingOverlay>

      {editingKey && (
        <TemplateEditor
          brandId={brandId}
          templateKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSaved={() => { load(); }}
        />
      )}
    </PagePaper>
  );
}
